import { useState } from "react";
import { Menu, X, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { InternshipDomain } from "../types";
import HitasoftLogo from "./HitasoftLogo";

interface NavbarProps {
  allDomains: InternshipDomain[];
  selectedDomain: InternshipDomain;
  onSelectDomain: (domain: InternshipDomain) => void;
  openModal: (mode: "apply" | "brochure") => void;
  currentView: "home" | "about" | "track";
  onChangeView: (view: "home" | "about" | "track", targetId?: string) => void;
}

export default function Navbar({
  allDomains,
  selectedDomain,
  onSelectDomain,
  openModal,
  currentView,
  onChangeView,
}: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const handleNavClick = (view: "home" | "about" | "track", targetId?: string) => {
    onChangeView(view, targetId);
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/50 bg-[#FFFFFF]/95 backdrop-blur-xl" id="app-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-18 flex items-center justify-between gap-4">
        
        {/* Brand Logo - Clickable to return Home */}
        <button 
          onClick={() => handleNavClick("home")} 
          className="text-left focus:outline-none cursor-pointer flex-shrink-0 flex items-center min-h-[48px]"
          title="Return to Home Screen"
          aria-label="Hitasoft logo home navigation"
        >
          <div className="scale-90 sm:scale-100 origin-left">
            <HitasoftLogo lightMode={true} />
          </div>
        </button>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-6 text-xs font-bold text-slate-800" aria-label="Desktop main navigation">
          <button
            onClick={() => handleNavClick("home")}
            className={`transition-all duration-200 cursor-pointer py-1.5 px-1 border-b-2 tracking-wide uppercase min-h-[44px] flex items-center ${
              currentView === "home" 
                ? "text-orange-650 border-orange-650 font-extrabold" 
                : "text-slate-600 border-transparent hover:text-orange-650 hover:border-slate-300 font-semibold"
            }`}
          >
            Home
          </button>
          <button
            onClick={() => handleNavClick("home", "internships")}
            className="transition-all duration-200 cursor-pointer py-1.5 px-1 border-b-2 border-transparent text-slate-600 hover:text-orange-650 hover:border-slate-300 tracking-wide uppercase min-h-[44px] flex items-center font-semibold"
          >
            Learning
          </button>
          <button
            onClick={() => handleNavClick("home", "internship-gallery-section")}
            className="transition-all duration-200 cursor-pointer py-1.5 px-1 border-b-2 border-transparent text-slate-600 hover:text-orange-650 hover:border-slate-300 tracking-wide uppercase min-h-[44px] flex items-center font-semibold"
          >
            Gallery
          </button>
          <button
            onClick={() => handleNavClick("home", "portal-testimonials")}
            className="transition-all duration-200 cursor-pointer py-1.5 px-1 border-b-2 border-transparent text-slate-600 hover:text-orange-650 hover:border-slate-300 tracking-wide uppercase min-h-[44px] flex items-center font-semibold"
          >
            Testimonials
          </button>
          <button
            onClick={() => handleNavClick("about")}
            className={`transition-all duration-200 cursor-pointer py-1.5 px-1 border-b-2 tracking-wide uppercase min-h-[44px] flex items-center ${
              currentView === "about" 
                ? "text-orange-650 border-orange-650 font-extrabold" 
                : "text-slate-600 border-transparent hover:text-orange-650 hover:border-slate-300 font-semibold"
            }`}
            id="navbar-about-trigger"
          >
            About Us
          </button>
        </nav>

        {/* Action Button Segment / Hamburger trigger */}
        <div className="flex items-center gap-3">
          {/* Quick Hotline Code (simulated static helper) */}
          <span className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-[10px] font-mono font-medium text-slate-600 shadow-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            ADMISSIONS OPEN
          </span>

          <a
            href="tel:+917708006989"
            className="hidden sm:flex items-center justify-center bg-gradient-to-r from-orange-650 to-orange-700 hover:from-orange-700 hover:to-orange-650 text-white px-5 py-2.5 rounded-full text-xs font-semibold transition-all shadow-md shadow-orange-500/10 cursor-pointer hover:-translate-y-0.5 active:translate-y-0 min-h-[44px]"
            id="navbar-apply-now"
          >
            Talk to an Expert
          </a>

          {/* Hamburger Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer min-w-[48px] min-h-[48px] flex items-center justify-center border border-slate-200/50"
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden border-t border-slate-200/60 bg-white overflow-hidden shadow-lg"
            id="navbar-mobile-drawer"
          >
            <div className="px-4 py-5 space-y-4">
              <nav className="flex flex-col space-y-1" aria-label="Mobile main navigation">
                <button
                  onClick={() => handleNavClick("home")}
                  className={`w-full text-left py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-colors min-h-[48px] ${
                    currentView === "home"
                      ? "bg-orange-500/10 text-orange-650"
                      : "text-slate-650 hover:bg-slate-50"
                  }`}
                >
                  Home
                </button>
                <button
                  onClick={() => handleNavClick("home", "internships")}
                  className="w-full text-left py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-colors min-h-[48px] text-slate-650 hover:bg-slate-50"
                >
                  Learning
                </button>
                <button
                  onClick={() => handleNavClick("home", "internship-gallery-section")}
                  className="w-full text-left py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-colors min-h-[48px] text-slate-650 hover:bg-slate-50"
                >
                  Gallery
                </button>
                <button
                  onClick={() => handleNavClick("home", "portal-testimonials")}
                  className="w-full text-left py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-colors min-h-[48px] text-slate-650 hover:bg-slate-50"
                >
                  Testimonials
                </button>
                <button
                  onClick={() => handleNavClick("about")}
                  className={`w-full text-left py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-colors min-h-[48px] ${
                    currentView === "about"
                      ? "bg-orange-500/10 text-orange-650"
                      : "text-slate-650 hover:bg-slate-50"
                  }`}
                >
                  About Us
                </button>
              </nav>

              <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
                <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-slate-50 rounded-xl border border-slate-200 text-xs font-mono font-semibold text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  HOTLINE Admissions Open
                </div>
                <a
                  href="tel:+917708006989"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center py-3.5 bg-gradient-to-r from-orange-650 to-orange-700 hover:from-orange-700 hover:to-orange-650 text-white rounded-xl text-xs font-bold tracking-wider uppercase transition-all shadow-md text-center cursor-pointer min-h-[48px]"
                >
                  Talk to an Expert
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sub-bar scrollable domain links with premium white/gray layout and active orange gradient */}
      <div className="relative w-full bg-[#F8FAFC]/95 border-t border-slate-200/40 shadow-xs">
        <div className="overflow-x-auto scrollbar-none py-2.5 w-full" id="subnavbar-scrollable-links">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3 whitespace-nowrap pr-12 md:pr-8">
            <span className="text-[11px] font-bold text-[#0F172A] tracking-widest uppercase font-mono mr-2 select-none flex items-center gap-1.5 shrink-0">
              <span className="text-orange-650 animate-pulse text-sm">⚡</span> QUICK TRACKS:
            </span>
            {allDomains.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  onSelectDomain(d);
                  onChangeView("track");
                }}
                className={`px-4 py-2 rounded-full text-xs tracking-wider transition-all duration-200 font-display font-extrabold uppercase cursor-pointer border min-h-[36px] flex items-center shrink-0 ${
                  selectedDomain.id === d.id && currentView === "track"
                    ? "bg-gradient-to-r from-orange-650 to-orange-700 text-white border-transparent shadow-sm shadow-orange-500/15 scale-102"
                    : "text-slate-600 bg-white border-slate-200 shadow-xs hover:text-orange-650 hover:border-orange-300"
                }`}
              >
                {d.shortSlug.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        
        {/* Mobile scroll button gradient overlay */}
        <div className="md:hidden absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#F8FAFC] to-transparent pointer-events-none flex items-center justify-end pr-2">
          <button 
            onClick={() => {
              const el = document.getElementById("subnavbar-scrollable-links");
              if (el) el.scrollBy({ left: 150, behavior: "smooth" });
            }}
            className="w-8 h-8 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:text-orange-600 pointer-events-auto cursor-pointer"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
